import React from 'react';
import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EChartsRenderer from '../../components/EChartsRenderer';
import type { PlanCapacity, PlanDemandTier } from '../../types/contactOptimization';

const DEMAND_TIER_OPACITY: Record<PlanDemandTier, number> = {
  low: 0.04,
  below_average: 0.09,
  above_average: 0.16,
  high: 0.26,
};

const DEMAND_TIER_LABELS: Record<PlanDemandTier, string> = {
  low: 'Quietest hours',
  below_average: 'Below-average demand',
  above_average: 'Above-average demand',
  high: 'Busiest hours',
};

/**
 * "Demand vs. staffed capacity, by hour" -- an ECharts combo chart. Bars
 * for staffed-vs-required, a continuous "required agents" line, and a
 * light background shading behind everything showing each hour's own
 * demand tier (this chart's own quartiles -- see `_demand_tiers_by_hour`
 * in service.py) as a reading aid, so the busiest stretch of the day is
 * visible at a glance, not just legible bar-by-bar.
 *
 * 2026-08-12: down to ONE required-agents line (there used to be a second,
 * separately-plotted "Historical required" line averaged over the full
 * call history -- retired once `plan.hours[].required_agents` itself
 * moved onto the same recent window as everything else on this chart; see
 * `get_plan_capacity`'s own docstring in service.py for why).
 */
export function CapacityChart({ plan }: { plan: PlanCapacity }) {
  const theme = useTheme();

  const rows = plan.hours.map((h) => {
    const required = h.required_agents ?? h.staffed_agents;
    const staffed = h.staffed_agents;
    const base = Math.min(staffed, required);
    const shortfall = Math.max(0, required - staffed);
    const surplus = Math.max(0, staffed - required);
    return {
      hour: h.hour,
      hour_label: h.hour_label,
      staffed,
      required,
      base,
      shortfall,
      surplus,
      demand_tier: h.demand_tier,
    };
  });

  const markAreaData = React.useMemo(() => {
    const areas: any[] = [];
    let i = 0;
    while (i < rows.length) {
      const tier = rows[i].demand_tier;
      let j = i;
      while (j + 1 < rows.length && rows[j + 1].demand_tier === tier) j++;
      if (tier) {
        areas.push([
          {
            xAxis: rows[i].hour_label,
            itemStyle: { color: alpha(theme.palette.primary.main, DEMAND_TIER_OPACITY[tier]) },
          },
          { xAxis: rows[j].hour_label },
        ]);
      }
      i = j + 1;
    }
    return areas;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, theme]);

  const option = React.useMemo(
    () => ({
      grid: { left: 56, right: 16, top: 36, bottom: 32 },
      legend: { top: 0, textStyle: { fontSize: 11 } },
      xAxis: { type: 'category', data: rows.map((r) => r.hour_label), axisLabel: { fontSize: 10 } },
      // Bold + a touch bigger than the other axis labels on purpose: this
      // is a headcount chart, not a rate chart -- every line and bar on it
      // is agents, never a percentage. The dashed line's own series name
      // repeats "(agents)" too (see below) so that reads true even at a
      // glance, without needing to check the axis first.
      yAxis: {
        type: 'value',
        name: 'Agents',
        nameTextStyle: { fontSize: 12, fontWeight: 700 },
        axisLabel: { fontSize: 11 },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          const idx = params[0]?.dataIndex ?? 0;
          const r = rows[idx];
          // ECharts hands back a ready-made colored-dot <span> per series
          // (`.marker`) that already matches that series' own legend
          // swatch -- reusing it here (rather than hardcoding colors a
          // second time) guarantees the tooltip and legend can never
          // drift out of sync with each other.
          const markerFor = (seriesName: string) => params.find((p) => p.seriesName === seriesName)?.marker ?? '';
          const gapText =
            r.shortfall > 0
              ? `short by ${r.shortfall.toFixed(1)} agents`
              : r.surplus > 0
                ? `surplus of ${r.surplus.toFixed(1)} agents`
                : 'matches demand';
          // No series backs the demand-tier shading (it's a markArea on
          // the "Forecast" line, not its own series), so there's no
          // `.marker` to reuse -- build a matching swatch by hand instead,
          // same color/opacity as the legend chips below the chart
          // (DEMAND_TIER_OPACITY).
          const tierSwatch = r.demand_tier
            ? `<span style="display:inline-block;margin-right:5px;width:9px;height:9px;border-radius:2px;vertical-align:middle;background-color:${alpha(theme.palette.primary.main, DEMAND_TIER_OPACITY[r.demand_tier])};border:1px solid ${theme.palette.divider};"></span>`
            : '';
          const tierText = r.demand_tier ? `<br/>${tierSwatch}${DEMAND_TIER_LABELS[r.demand_tier]} (relative to today)` : '';
          const gapMarker = r.shortfall > 0 ? markerFor('Short of demand') : r.surplus > 0 ? markerFor('More than needed') : '';
          return `<strong>${r.hour_label}</strong><br/>${markerFor('Staffed (up to demand)')}Staffed: ${r.staffed.toFixed(1)} agents<br/>${markerFor('Forecast (agents)')}Forecast: ~${r.required.toFixed(1)} agents — not a rate/percentage<br/>${gapMarker}${gapText}${tierText}`;
        },
      },
      series: [
        {
          name: 'Staffed (up to demand)',
          type: 'bar',
          stack: 'cap',
          data: rows.map((r) => +r.base.toFixed(2)),
          itemStyle: { color: theme.palette.primary.main, opacity: 0.55 },
        },
        {
          // Two series rather than one two-coloured "Gap": a single legend
          // chip cannot represent a series that is red in some hours and
          // green in others, and the chip's own colour matched neither.
          // Splitting them lets the legend say what each colour means.
          name: 'Short of demand',
          type: 'bar',
          stack: 'cap',
          data: rows.map((r) => +r.shortfall.toFixed(2)),
          itemStyle: { color: theme.palette.error.main, opacity: 0.75 },
        },
        {
          name: 'More than needed',
          type: 'bar',
          stack: 'cap',
          data: rows.map((r) => +r.surplus.toFixed(2)),
          itemStyle: { color: theme.palette.success.main, opacity: 0.65 },
        },
        {
          // "(agents)" is part of the NAME, not just the tooltip -- it's
          // what shows up in the compact legend chip up top, which is the
          // one place someone glancing at the chart on a call actually
          // reads before assuming a dashed line means a rate.
          name: 'Forecast (agents)',
          type: 'line',
          symbol: 'none',
          lineStyle: { type: 'dashed', width: 2, color: theme.palette.warning.main },
          data: rows.map((r) => +r.required.toFixed(2)),
          markArea: {
            silent: true,
            data: markAreaData,
          },
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, theme, markAreaData],
  );

  return <EChartsRenderer option={option} height={300} />;
}

export { DEMAND_TIER_LABELS };
